import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';

// Initialize Sentry only in production
import { initSentry } from './util/sentry';
if (process.env.NODE_ENV === 'production') {
  initSentry();
}


// jQuery shim must be exposed globally BEFORE Bootstrap is imported
// Using jQuery shim for Bootstrap compatibility (jQuery removed from bundle)
import $ from './utils/jqueryShim';
window.$ = window.jQuery = $;

// Suppress third-party library warnings that we cannot fix
const originalWarn = console.warn;
const originalError = console.error;

const suppressedWarnings = [
  'Support for defaultProps will be removed',
  'componentWillUpdate has been renamed',
  'componentWillMount has been renamed',
  'componentWillReceiveProps has been renamed',
  'findDOMNode is deprecated',
  'Userflow.js error',
  'TrackJS: agent must be installed',
  'Highcharts warning',
  'accessibility.js',
  'circular references', // antd Form isEqual on moment/dayjs objects
  'is not connected to any Form element', // antd useForm called before Form mounts
];

console.warn = (...args) => {
  const message = args[0]?.toString() || '';
  if (suppressedWarnings.some(w => message.includes(w))) return;
  originalWarn.apply(console, args);
};

console.error = (...args) => {
  const message = args[0]?.toString() || '';
  if (suppressedWarnings.some(w => message.includes(w))) return;
  originalError.apply(console, args);
};

import React, { lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import userflow from 'userflow.js';
import configData from './config/loader';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min';

// import './index.scss'; // Standard version

// import App from './components/App';
import store from './store';
import {
  MetronicLayoutProvider,
  MetronicSubheaderProvider,
  MetronicSplashScreenProvider,
} from './_metronic/layout';
import { setHeaders } from './components/Global/globalLib';
import { initCsrfToken } from './util/csrf';

const App = lazy(() => import('./components/App'));

// SECURITY: Initialize CSRF token on app load
// This fetches the XSRF-TOKEN cookie from the server
initCsrfToken().catch(() => {
  // Silently fail - CSRF protection will work when cookie is eventually set
});
const DefaultStyling = lazy(() => import('./config/customStyles/defaultStyle'));
const RadStyling = lazy(() => import('./config/customStyles/radStyle'));
// Suppress Userflow errors from triggering the webpack dev server overlay
window.addEventListener('error', (event) => {
  if (event.message?.includes('Userflow') || event.filename?.includes('userflow')) {
    event.preventDefault();
    return true;
  }
});

// Only initialize Userflow in production to avoid dev server overlay errors
const isProduction = process.env.REACT_APP_API_ENV === 'production';
const isStaging = process.env.REACT_APP_API_ENV === 'staging';


if ((isStaging || isProduction) && configData.userFlowToken && configData.userFlowToken !== null) { 
  try {
    userflow.init(configData.userFlowToken);
  } catch (e) {
    // Silently ignore Userflow initialization errors
  }
}

setHeaders(configData);

// eslint-disable-next-line react/prop-types
const StyleSelector = ({ children }) => {
  return (
    // eslint-disable-next-line react/jsx-filename-extension
    <>
      {configData.style === 'rad' && <RadStyling />}
      {configData.style === 'default' && <DefaultStyling />}

      {children}
    </>
  );
};

const container = document.getElementById('root');
const root = createRoot(container);
root.render(
  <Suspense fallback={<></>}>
    <StyleSelector>
      <MetronicLayoutProvider>
        <MetronicSplashScreenProvider>
          <MetronicSubheaderProvider>
            <Provider store={store}>
              <App />
            </Provider>
          </MetronicSubheaderProvider>
        </MetronicSplashScreenProvider>
      </MetronicLayoutProvider>
    </StyleSelector>
  </Suspense>
);
